/**
 * Connected wrapper for `GithubTipBanner`. Wires `onConnect` to the standard
 * QF OAuth flow via `useOAuthHandlers`, which fires `CORE - 3P Auth Clicked`
 * on click and `CORE - 3P Auth Completed` on success — both filtered to
 * `connectorId = github` for apples-to-apples comparison with other QF
 * connect surfaces (OAuth banner, app filter, bulk connect).
 */
import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { ProductKeys } from '../../../../../common/constants/product-keys';
import { OAuthSource } from '../../../../../common/utils/events/oauth-events';
import { ErrorBoundary } from '../../../../../controllers/error-boundary';
import { useIsUserOAuthed, useOAuthHandlers } from '../../../../../controllers/oauth';

import { messages as bannerMessages } from './messages';

import { GithubTipBanner } from './index';

type ConnectedGithubTipBannerProps = {
	query: string;
	disableGninTrigger?: boolean;
	withTrailingDivider?: boolean;
	useNegativeMarginTop?: boolean;
};

export const ConnectedGithubTipBanner = ({
	query,
	disableGninTrigger = false,
	withTrailingDivider = false,
	useNegativeMarginTop = false,
}: ConnectedGithubTipBannerProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const { userNeedsOAuth, outboundAuthUrl } = useIsUserOAuthed(ProductKeys.Github);

	const { onAuthorize } = useOAuthHandlers({
		product: ProductKeys.Github,
		successFlagTitle: formatMessage(bannerMessages.oauthSuccessFlagTitle),
		successFlagDescription: formatMessage(bannerMessages.oauthSuccessFlagDescription),
		showUnderstoodButton: true,
		failFlagTitle: formatMessage(bannerMessages.oauthFailFlagTitle),
		outboundAuthUrl: outboundAuthUrl || '',
		isOnSearchDialog: true,
		query,
	});

	const handleConnect = useCallback(() => {
		onAuthorize(OAuthSource.quickFindGithubTipBanner);
	}, [onAuthorize]);

	if (!userNeedsOAuth || !outboundAuthUrl) {
		return null;
	}

	return (
		<ErrorBoundary section="QuickFindGithubTipBanner">
			<GithubTipBanner
				query={query}
				onConnect={handleConnect}
				disableGninTrigger={disableGninTrigger}
				withTrailingDivider={withTrailingDivider}
				useNegativeMarginTop={useNegativeMarginTop}
			/>
		</ErrorBoundary>
	);
};
